
import { fetchPosts, fetchPostBySlug } from '@/services/wordpress';
import { BlogPost } from '@/types/blog';
import { getPostsFromCache, getSinglePostFromCaches, savePostsToCaches, saveSinglePostToCaches } from './cacheUtils';
import { debouncedPrefetchPosts, debouncedPrefetchSinglePost } from './prefetchUtils';

/**
 * Recupera i post del blog con caching e prefetching
 * - Utilizza l'API WordPress per ricerca e filtri
 * - Implementa ricerca lato server per migliori prestazioni
 */
export const fetchBlogPosts = async (
  page: number,
  perPage: number,
  searchTerm?: string,
  category?: string | number | null,
  forceRefresh = false
): Promise<BlogPost[]> => {
  // Pulizia parametri
  const cleanSearchTerm = searchTerm?.trim() || '';
  
  // Gestione coerente della categoria come ID numerico
  let categoryId: number | undefined = undefined;
  
  if (category !== undefined && category !== null && category !== '') {
    if (typeof category === 'string') {
      const parsed = parseInt(category.trim(), 10);
      if (!isNaN(parsed) && parsed > 0) {
        categoryId = parsed;
      }
    } else if (typeof category === 'number' && category > 0) {
      categoryId = category;
    }
  }
  
  console.log(`[fetchUtils] Parametri di ricerca: searchTerm="${cleanSearchTerm}", categoryId=${categoryId}, categoria originale=${category} (${typeof category})`);
  
  try {
    // Utilizziamo l'API WordPress per ricerca e filtri (tutto lato server)
    const fetchParams = {
      page,
      perPage,
      search: cleanSearchTerm, // Passa il termine di ricerca direttamente all'API
      category: categoryId // Passa l'ID numerico della categoria all'API (o undefined per tutte)
    };
    
    const results = await fetchPosts(fetchParams);
    
    return results;
  } catch (error) {
    console.error('[fetchUtils] Errore nel recupero dei post del blog:', error);
    return [];
  }
};

/**
 * Recupera un singolo post del blog tramite slug con caching e prefetching
 */
export const fetchBlogPostBySlug = async (slug: string): Promise<BlogPost | null> => {
  if (!slug) return null;
  
  const cleanSlug = slug.trim();
  console.log(`[fetchUtils] Recupero post del blog per slug: "${cleanSlug}"`);
  
  // Prova a ottenere il post dalla cache
  const { post, shouldPrefetch } = getSinglePostFromCaches<BlogPost>(cleanSlug);
  
  if (post !== null) {
    // Se abbiamo un post in cache ma è vecchio, prefetch in background
    if (shouldPrefetch) {
      console.log(`[fetchUtils] Cache vecchia, prefetch in background per: ${cleanSlug}`);
      debouncedPrefetchSinglePost(cleanSlug);
    }
    return post;
  }
  
  // Se non ci sono dati in cache, recupera dati freschi
  console.log(`[fetchUtils] Nessun dato in cache trovato, recupero post del blog fresco per slug: ${cleanSlug}`);
  const fetchedPost = await fetchPostBySlug(cleanSlug);
  
  // Salva in cache solo se abbiamo trovato un post valido
  if (fetchedPost) {
    saveSinglePostToCaches(cleanSlug, fetchedPost);
  }
  
  return fetchedPost;
};

// Re-export per compatibilità
export { getPostsFromCache, getSinglePostFromCaches, savePostsToCaches, saveSinglePostToCaches };
